// StationeryProLayout.js - Stationery Shop Theme
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  StoreLogo,
  CartBtn,
  TabContent,
  StoreFooter,
  InlineEditableImage,
  AWS_URL,
  FaHeart,
} from "./ThemeLayoutHelpers";

// Custom InlineEditableTextWithNav component
const InlineEditableTextWithNav = ({
  p,
  field,
  value,
  className = "",
  style = {},
  children,
  onNavigate,
  navPage,
  isInModal = false,
}) => {
  const isEditing = p.previewEditMode && p.activeEditField === field;
  const isEditable = typeof p.onActivateField === "function";

  const getCurrentValue = () => {
    if (
      p.storeTheme &&
      p.storeTheme[field] !== undefined &&
      p.storeTheme[field] !== ""
    ) {
      return p.storeTheme[field];
    }
    // Also check p.themeText if available (for preview mode)
    if (p.themeText && p.themeText[field] !== undefined && p.themeText[field] !== "") {
      return p.themeText[field];
    }
    return value;
  };

  const [localValue, setLocalValue] = useState(getCurrentValue());

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(getCurrentValue());
    }
  }, [p.storeTheme, field, value, isEditing, p.themeText]);

  const handleActivate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const currentVal = getCurrentValue();
    setLocalValue(currentVal);
    if (typeof p.onActivateField === "function") {
      p.onActivateField(field);
    }
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
  };

  const handleSaveAndClose = () => {
    if (typeof p.onPreviewTextChange === "function") {
      p.onPreviewTextChange(field, localValue);
    }
    if (typeof p.onDeactivateField === "function") {
      p.onDeactivateField();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      handleSaveAndClose();
      if (!isInModal && onNavigate && navPage) {
        onNavigate(navPage);
      }
    }
  };

  const handleBlur = () => {
    handleSaveAndClose();
  };

  if (isEditing) {
    const inputClass =
      "w-full rounded-2xl border border-blue-200 bg-white/90 px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-blue-700 dark:bg-gray-800 dark:text-gray-100";
    const multiline =
      field.toLowerCase().includes("subtitle") ||
      field.toLowerCase().includes("description") ||
      field.toLowerCase().includes("overview") ||
      field.toLowerCase().includes("content");

    return multiline ? (
      <textarea
        autoFocus
        rows={4}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    ) : (
      <input
        autoFocus
        type='text'
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    );
  }

  const displayValue = p.storeTheme?.[field] || p.themeText?.[field] || value;

  return (
    <span
      className={`${className} ${isEditable ? "cursor-pointer inline-block" : "inline-block"}`}
      style={{ whiteSpace: "pre-wrap", ...style }}
      onClick={isEditable ? handleActivate : undefined}
      title={p.previewEditMode ? "Edit text" : undefined}
    >
      {displayValue}
    </span>
  );
};

export default function StationeryProLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#2563eb";
  const secondary = T.secondaryColor || "#60a5fa";

  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [visibleProducts, setVisibleProducts] = useState([]);
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [animationComplete, setAnimationComplete] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");

  const heroRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setRefreshKey((prev) => prev + 1);
  }, [p.storeTheme]);

  // Stationery hero images - DIRECTLY FROM CONFIG
  const stationeryHeroImageConfigs = [
    {
      field: "themePreviewImage",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/624be9de-8796-47d6-bf3e-d5cbb813dc92_Premium_Notebook_(Set_of_5).jpg",
    },
    {
      field: "stationeryHeroImage2",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/771371ca-628c-44b6-9c68-5e21485697cf_ballpen.jpg",
    },
    {
      field: "stationeryHeroImage3",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/355fa0d6-e607-4e73-92ea-7fd0cabd22e1_Pencil_Set.avif",
    },
    {
      field: "stationeryHeroImage4",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/d893c744-6726-4652-9388-591ee00e5874_Pencil_Case.jpg",
    },
    {
      field: "stationeryHeroImage5",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/14cc5b3a-7ba7-47be-aad9-7535a272354b_Geometry_Box.jpg",
    },
  ];

  // Stationery Products - Added imageField
  const stationeryProducts = [
    // School Supplies
    {
      id: 1,
      nameKey: "product1_name",
      name: "Premium Notebook (Set of 5)",
      priceKey: "product1_price",
      price: 249,
      originalPriceKey: "product1_original_price",
      originalPrice: 399,
      imageField: "product1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/624be9de-8796-47d6-bf3e-d5cbb813dc92_Premium_Notebook_(Set_of_5).jpg",
      categoryKey: "product1_category",
      category: "School Supplies",
      subCategory: "Notebooks",
      descriptionKey: "product1_description",
      description:
        "High-quality A4 ruled notebooks with premium paper and durable cover.",
      badge: "Bestseller",
      badgeColor: "#2563eb",
      brand: "Classmate",
      pages: "200",
      binding: "Spiral",
      paperType: "70 GSM",
    },
    {
      id: 2,
      nameKey: "product2_name",
      name: "Ball Pen Pack (10 pcs)",
      priceKey: "product2_price",
      price: 99,
      originalPriceKey: "product2_original_price",
      originalPrice: 149,
      imageField: "product2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/771371ca-628c-44b6-9c68-5e21485697cf_ballpen.jpg",
      categoryKey: "product2_category",
      category: "School Supplies",
      subCategory: "Pens",
      descriptionKey: "product2_description",
      description:
        "Smooth writing ball pens with comfortable grip and long-lasting ink.",
      badge: "Popular",
      badgeColor: "#f59e0b",
      brand: "Reynolds",
      inkColor: "Blue",
      tip: "0.7mm",
    },
    {
      id: 3,
      nameKey: "product3_name",
      name: "Pencil Set (12 pcs)",
      priceKey: "product3_price",
      price: 79,
      originalPriceKey: "product3_original_price",
      originalPrice: 119,
      imageField: "product3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/355fa0d6-e607-4e73-92ea-7fd0cabd22e1_Pencil_Set.avif",
      categoryKey: "product3_category",
      category: "School Supplies",
      subCategory: "Pencils",
      descriptionKey: "product3_description",
      description: "Premium HB pencils with soft lead and ergonomic grip.",
      badge: "Value Pack",
      badgeColor: "#16a34a",
      brand: "Faber-Castell",
      hardness: "HB",
      material: "Wood",
    },
    {
      id: 4,
      nameKey: "product4_name",
      name: "Geometry Box",
      priceKey: "product4_price",
      price: 199,
      originalPriceKey: "product4_original_price",
      originalPrice: 299,
      imageField: "product4_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/14cc5b3a-7ba7-47be-aad9-7535a272354b_Geometry_Box.jpg",
      categoryKey: "product4_category",
      category: "School Supplies",
      subCategory: "Geometry",
      descriptionKey: "product4_description",
      description: "Complete geometry box with compass, protractor, and scale.",
      badge: "Essential",
      badgeColor: "#8b5cf6",
      brand: "Camlin",
      pieces: "12",
      material: "Stainless Steel",
    },

    // Office Supplies
    {
      id: 5,
      nameKey: "product5_name",
      name: "Stapler with Pins",
      priceKey: "product5_price",
      price: 149,
      originalPriceKey: "product5_original_price",
      originalPrice: 219,
      imageField: "product5_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/932b5db3-99f4-490f-9e3f-b9a8ed905514_Stapler_with_Pins.jpg",
      categoryKey: "product5_category",
      category: "Office Supplies",
      subCategory: "Staplers",
      descriptionKey: "product5_description",
      description: "Heavy-duty stapler with 1000 pins for office use.",
      badge: "Office Essential",
      badgeColor: "#2563eb",
      brand: "Kangaro",
      capacity: "25 sheets",
      color: "Black",
    },
    {
      id: 6,
      nameKey: "product6_name",
      name: "File Folder (50 pcs)",
      priceKey: "product6_price",
      price: 299,
      originalPriceKey: "product6_original_price",
      originalPrice: 449,
      imageField: "product6_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/d919843e-ddd0-4d4c-bedc-2ce7466917e8_File_Folder.webp",
      categoryKey: "product6_category",
      category: "Office Supplies",
      subCategory: "Files & Folders",
      descriptionKey: "product6_description",
      description:
        "Premium quality file folders for organized document storage.",
      badge: "Organizer",
      badgeColor: "#f59e0b",
      brand: "Paperclip",
      material: "Cardboard",
      size: "A4",
    },
    {
      id: 7,
      nameKey: "product7_name",
      name: "Sticky Notes (Pack of 5)",
      priceKey: "product7_price",
      price: 89,
      originalPriceKey: "product7_original_price",
      originalPrice: 129,
      imageField: "product7_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/e90b9ae1-fd17-4a02-a654-94af696de6f3_Sticky_Notes.jpg",
      categoryKey: "product7_category",
      category: "Office Supplies",
      subCategory: "Sticky Notes",
      descriptionKey: "product7_description",
      description: "Colorful sticky notes for reminders and quick notes.",
      badge: "Colorful",
      badgeColor: "#8b5cf6",
      brand: "Post-it",
      sheets: "100",
      colors: "5",
    },
    {
      id: 8,
      nameKey: "product8_name",
      name: "Tape Dispenser",
      priceKey: "product8_price",
      price: 129,
      originalPriceKey: "product8_original_price",
      originalPrice: 189,
      imageField: "product8_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/3013d285-67ab-4ebe-b9ff-ac549e8cb984_Tape_Dispenser.webp",
      categoryKey: "product8_category",
      category: "Office Supplies",
      subCategory: "Tapes",
      descriptionKey: "product8_description",
      description:
        "Desktop tape dispenser with non-slip base and precision cutting.",
      badge: "Durable",
      badgeColor: "#16a34a",
      brand: "Scotch",
      tapeWidth: "12mm",
      material: "Plastic",
    },

    // Art & Craft
    {
      id: 9,
      nameKey: "product9_name",
      name: "Watercolor Set (24 colors)",
      priceKey: "product9_price",
      price: 399,
      originalPriceKey: "product9_original_price",
      originalPrice: 599,
      imageField: "product9_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/e030dea8-0e14-412e-a4cd-ba05d1e56b12_Watercolor_Set.webp",
      categoryKey: "product9_category",
      category: "Art & Craft",
      subCategory: "Colors",
      descriptionKey: "product9_description",
      description:
        "Premium watercolor set with vibrant pigments and brush included.",
      badge: "Artist Choice",
      badgeColor: "#dc2626",
      brand: "Camel",
      colors: "24",
      include: "Brush",
    },
    {
      id: 10,
      nameKey: "product10_name",
      name: "Sketchbook A4",
      priceKey: "product10_price",
      price: 199,
      originalPriceKey: "product10_original_price",
      originalPrice: 299,
      imageField: "product10_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/659cf1db-da7e-4b24-a96c-1bb76d3439fd_Sketchbook.webp",
      categoryKey: "product10_category",
      category: "Art & Craft",
      subCategory: "Sketchbooks",
      descriptionKey: "product10_description",
      description: "Premium sketchbook with 180 GSM paper for all media.",
      badge: "Premium",
      badgeColor: "#8b5cf6",
      brand: "Artiste",
      pages: "100",
      paperWeight: "180 GSM",
    },
    {
      id: 11,
      nameKey: "product11_name",
      name: "Color Pencils (36 colors)",
      priceKey: "product11_price",
      price: 299,
      originalPriceKey: "product11_original_price",
      originalPrice: 449,
      imageField: "product11_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/52ff6799-a3a1-4044-ae33-6e7a3ab4237c_Color_Pencils.jpg",
      categoryKey: "product11_category",
      category: "Art & Craft",
      subCategory: "Color Pencils",
      descriptionKey: "product11_description",
      description: "Professional color pencil set with rich, blendable colors.",
      badge: "Vibrant",
      badgeColor: "#f59e0b",
      brand: "Faber-Castell",
      colors: "36",
      type: "Oil-based",
    },
    {
      id: 12,
      nameKey: "product12_name",
      name: "Craft Paper Set (50 sheets)",
      priceKey: "product12_price",
      price: 149,
      originalPriceKey: "product12_original_price",
      originalPrice: 219,
      imageField: "product12_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/7df53e31-88fc-4af5-9720-90bd995c9c47_Craft_Paper_Set.avif",
      categoryKey: "product12_category",
      category: "Art & Craft",
      subCategory: "Craft Paper",
      descriptionKey: "product12_description",
      description: "Assorted craft paper pack for all your creative projects.",
      badge: "Creative",
      badgeColor: "#16a34a",
      brand: "Crafty",
      sheets: "50",
      sizes: "A4",
    },

    // Stationery Accessories
    {
      id: 13,
      nameKey: "product13_name",
      name: "Pencil Case",
      priceKey: "product13_price",
      price: 129,
      originalPriceKey: "product13_original_price",
      originalPrice: 189,
      imageField: "product13_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/d893c744-6726-4652-9388-591ee00e5874_Pencil_Case.jpg",
      categoryKey: "product13_category",
      category: "Accessories",
      subCategory: "Pencil Cases",
      descriptionKey: "product13_description",
      description:
        "Stylish pencil case with multiple compartments for organization.",
      badge: "Organized",
      badgeColor: "#8b5cf6",
      brand: "Camlin",
      material: "Fabric",
      color: "Blue",
    },
    {
      id: 14,
      nameKey: "product14_name",
      name: "Eraser Set (5 pcs)",
      priceKey: "product14_price",
      price: 49,
      originalPriceKey: "product14_original_price",
      originalPrice: 79,
      imageField: "product14_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/23bd0885-1069-4755-bbe9-377eeb9e1b84_Eraser_Set.webp",
      categoryKey: "product14_category",
      category: "Accessories",
      subCategory: "Erasers",
      descriptionKey: "product14_description",
      description: "Premium erasers that don't smudge and last longer.",
      badge: "Essential",
      badgeColor: "#2563eb",
      brand: "Faber-Castell",
      type: "Pencil",
      quantity: "5",
    },
  ];

  // Categories
  const productCategories = [
    { key: "all", icon: "📚", label: "All Products", color: "#2563eb" },
    { key: "school", icon: "✏️", label: "School Supplies", color: "#f59e0b" },
    { key: "office", icon: "📁", label: "Office Supplies", color: "#2563eb" },
    { key: "art", icon: "🎨", label: "Art & Craft", color: "#dc2626" },
    { key: "accessories", icon: "🎒", label: "Accessories", color: "#8b5cf6" },
  ];

  // Learning Guides - Added imageField
  const learningGuides = [
    {
      id: 1,
      titleKey: "guide1_title",
      title: "How to Choose the Right Notebook",
      descKey: "guide1_desc",
      desc: "Find the perfect notebook for your needs.",
      imageField: "guide1_image",
      image: "https://images.unsplash.com/photo-1527864550417-7fd91fc51a46?w=600&q=80",
      steps: [
        "Consider the paper quality (GSM)",
        "Choose the right binding (spiral, perfect)",
        "Select the appropriate size",
        "Check for features like pockets or dividers",
        "Pick a design that inspires you",
      ],
      difficulty: "Beginner",
    },
    {
      id: 2,
      titleKey: "guide2_title",
      title: "Art Techniques for Beginners",
      descKey: "guide2_desc",
      desc: "Start your artistic journey with these tips.",
      imageField: "guide2_image",
      image: "https://images.unsplash.com/photo-1513542789411-b6a5d4f31634?w=600&q=80",
      steps: [
        "Start with basic shapes and forms",
        "Practice shading and blending",
        "Experiment with different mediums",
        "Learn color theory basics",
        "Regular practice is key",
      ],
      difficulty: "Beginner",
    },
    {
      id: 3,
      titleKey: "guide3_title",
      title: "Office Organization Tips",
      descKey: "guide3_desc",
      desc: "Keep your workspace tidy and productive.",
      imageField: "guide3_image",
      image: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=600&q=80",
      steps: [
        "Use filing systems for documents",
        "Keep supplies within reach",
        "Label everything clearly",
        "Declutter regularly",
        "Create a system that works for you",
      ],
      difficulty: "Beginner",
    },
  ];

  // Blog Posts - Added imageField
  const editorialPosts = [
    {
      id: 1,
      titleKey: "post1_title",
      title: "The Joy of Handwriting",
      descKey: "post1_desc",
      desc: "Why handwriting matters in the digital age.",
      fullContentKey: "post1_content",
      fullContent:
        "In a world of digital communication, the art of handwriting remains special. From improving cognitive function to creating personal connections, discover why putting pen to paper is still valuable today.",
      imageField: "post1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/863cf7dc-1fbd-40e6-af6b-a9cc1159eb51_The_Joy_of_Handwriting.jpg",
      dateKey: "post1_date",
      date: "March 15, 2024",
      authorKey: "post1_author",
      author: "Penmanship Pro",
      category: "Writing",
    },
    {
      id: 2,
      titleKey: "post2_title",
      title: "Back to School Guide",
      descKey: "post2_desc",
      desc: "Essential supplies for the new school year.",
      fullContentKey: "post2_content",
      fullContent:
        "The new school year brings new opportunities. Our comprehensive guide covers everything students need, from basics like notebooks and pens to creative supplies and organizational tools.",
      imageField: "post2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/771371ca-628c-44b6-9c68-5e21485697cf_ballpen.jpg",
      dateKey: "post2_date",
      date: "March 10, 2024",
      authorKey: "post2_author",
      author: "Teacher's Pet",
      category: "Education",
    },
    {
      id: 3,
      titleKey: "post3_title",
      title: "Art Therapy Benefits",
      descKey: "post3_desc",
      desc: "How creative expression improves mental health.",
      fullContentKey: "post3_content",
      fullContent:
        "Art therapy is a powerful tool for mental wellness. From reducing stress to improving focus, discover how creative expression can transform your well-being and bring joy to your daily life.",
      imageField: "post3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/de75e355-b676-475f-bcb4-4482eec4035b_art_pencil.jpg",
      dateKey: "post3_date",
      date: "March 5, 2024",
      authorKey: "post3_author",
      author: "Art Healer",
      category: "Wellness",
    },
  ];

  // Navigation items
  const navItems = [
    { key: "nav_home", label: "HOME", page: "home" },
    { key: "nav_school", label: "SCHOOL", page: "school" },
    { key: "nav_office", label: "OFFICE", page: "office" },
    { key: "nav_art", label: "ART & CRAFT", page: "art" },
    { key: "nav_accessories", label: "ACCESSORIES", page: "accessories" },
    { key: "nav_journal", label: "JOURNAL", page: "journal" },
    { key: "nav_about", label: "ABOUT", page: "about" },
  ];

  // Top bar messages
  const topBarMessages = [
    "📚 STATIONERY & SCHOOL SUPPLIES",
    "✦ FREE DELIVERY ON FIRST ORDER",
    "✏️ PREMIUM QUALITY PRODUCTS",
    "✦ BULK DISCOUNTS AVAILABLE",
  ];

  // Floating particles
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: 3 + Math.random() * 5,
    delay: Math.random() * 5,
    duration: 5 + Math.random() * 5,
    opacity: 0.05 + Math.random() * 0.15,
  }));

  // Auto-rotate hero images - USING CONFIG DIRECTLY
  useEffect(() => {
    if (p.previewEditMode) return;
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % stationeryHeroImageConfigs.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [p.previewEditMode]);

  // Animate products
  useEffect(() => {
    const products = p.filteredProducts || [];
    if (products.length === 0) return;

    setTimeout(() => setAnimationComplete(true), 300);

    products.forEach((_, index) => {
      setTimeout(
        () => {
          setVisibleProducts((prev) => [...prev, index]);
        },
        index * 80 + 500,
      );
    });
  }, [p.filteredProducts]);

  // Mouse move for parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 15,
        y: (e.clientY / window.innerHeight - 0.5) * 10,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigateTo = useCallback(
    (page, event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (p.previewEditMode && p.activeEditField) {
        return;
      }
      setActivePage(page);
      setMobileMenuOpen(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [p.previewEditMode, p.activeEditField],
  );

  const handleNavigateAfterEdit = useCallback((page) => {
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const openBlogModal = useCallback(
    (post, event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (p.previewEditMode && p.activeEditField) {
        return;
      }
      setSelectedPost(post);
    },
    [p.previewEditMode, p.activeEditField],
  );

  const closeBlogModal = useCallback(() => {
    setSelectedPost(null);
  }, []);

  const openGuideModal = useCallback(
    (guide, event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (p.previewEditMode && p.activeEditField) {
        return;
      }
      setSelectedGuide(guide);
    },
    [p.previewEditMode, p.activeEditField],
  );

  const closeGuideModal = useCallback(() => {
    setSelectedGuide(null);
  }, []);

  const handleBackdropClick = useCallback(
    (event) => {
      if (event.target === event.currentTarget) {
        if (p.previewEditMode && p.activeEditField) {
          return;
        }
        setSelectedProduct(null);
        setSelectedPost(null);
        setSelectedGuide(null);
      }
    },
    [p.previewEditMode, p.activeEditField],
  );

  const handleEditHeroImage = useCallback((event, field = null) => {
    event.preventDefault();
    event.stopPropagation();
    if (!p.previewEditMode) {
      return;
    }
    const targetField = field || stationeryHeroImageConfigs[activeSlide]?.field || "themePreviewImage";
    const targetIndex = stationeryHeroImageConfigs.findIndex((image) => image.field === targetField);
    if (targetIndex >= 0) {
      setActiveSlide(targetIndex);
    }
    if (typeof p.onImageFieldClick === "function") {
      p.onImageFieldClick(targetField, event);
      return;
    }
    if (typeof p.onActivateField === "function") {
      p.onActivateField(targetField);
    }
  }, [activeSlide, stationeryHeroImageConfigs, p]);

  const copy = {
    eyebrow: p.storeTheme?.heroEyebrow || "STATIONERY PRO",
    title: p.storeTheme?.heroTitle || "Write Your Story",
    subtitle:
      p.storeTheme?.heroSubtitle ||
      "Premium stationery for school, office, and creative expression",
    primaryCta: p.storeTheme?.primaryCtaText || "Shop Now",
    secondaryCta: p.storeTheme?.secondaryCtaText || "View Guides",
  };

  // Get editable products with imageField
  const editableStationeryProducts = stationeryProducts.map((product) => ({
    ...product,
    image: p.storeTheme?.[product.imageField] || product.image,
  }));

  // Get editable guides with imageField
  const editableLearningGuides = learningGuides.map((guide) => ({
    ...guide,
    image: p.storeTheme?.[guide.imageField] || guide.image,
  }));

  // Get editable posts with imageField
  const editableEditorialPosts = editorialPosts.map((post) => ({
    ...post,
    image: p.storeTheme?.[post.imageField] || post.image,
  }));

  // Get filtered items based on category
  const getFilteredItems = (category) => {
    if (category === "all") return editableStationeryProducts;
    const map = {
      school: "School Supplies",
      office: "Office Supplies",
      art: "Art & Craft",
      accessories: "Accessories",
    };
    return editableStationeryProducts.filter((item) => item.category === map[category]);
  };

  const filteredItems = getFilteredItems(activeCategory);

  // Get card entrance animation
  const getCardEntrance = (index) => {
    const directions = [
      { x: -40, y: -15, rotate: -3 },
      { x: 40, y: -15, rotate: 3 },
      { x: -40, y: 15, rotate: -2 },
      { x: 40, y: 15, rotate: 2 },
    ];
    return directions[index % directions.length];
  };

  return (
    <div
      key={refreshKey}
      className='w-full min-h-screen bg-blue-50 overflow-x-hidden'
      style={{ fontFamily: T.fontFamily || "'Poppins', sans-serif" }}
    >
      {/* Floating Particles */}
      <div className='fixed inset-0 pointer-events-none overflow-hidden z-0'>
        {particles.map((particle) => (
          <div
            key={particle.id}
            className='absolute rounded-full animate-float-particle'
            style={{
              left: `${particle.left}%`,
              top: `${particle.top}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              background: `radial-gradient(circle, ${primary}${Math.floor(20 + Math.random() * 40).toString(16)}, transparent)`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`,
              opacity: particle.opacity,
            }}
          />
        ))}
      </div>

      {/* Top Bar */}
      <div className='relative bg-[#1a365d] text-blue-200 py-2 overflow-hidden'>
        <div className='whitespace-nowrap animate-marquee'>
          {topBarMessages.map((msg, idx) => (
            <span
              key={idx}
              className='inline-block mx-8 text-xs tracking-wider text-white/80'
            >
              <InlineEditableTextWithNav
                p={p}
                field={`top_bar_message_${idx + 1}`}
                value={msg}
                onNavigate={handleNavigateAfterEdit}
                navPage='home'
              />
            </span>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <nav
        className={`sticky top-0 z-50 transition-all duration-500 ${isScrolled ? "bg-[#1a365d]/95 backdrop-blur-xl shadow-lg py-2" : "bg-[#1a365d]/90 backdrop-blur-sm py-4"}`}
      >
        <div className='w-full px-4 md:px-8 flex items-center justify-between'>
          <div
            className='flex items-center gap-3 cursor-pointer'
            onClick={(e) => navigateTo("home", e)}
          >
            <StoreLogo
              storeInfo={p.storeInfo}
              border={`border-2 ${isScrolled ? "border-blue-400" : "border-blue-300"}`}
            />
            <span className='font-bold text-xl tracking-tight text-white'>
              <InlineEditableTextWithNav
                p={p}
                field='businessName'
                value={p.storeInfo?.businessName || "Stationery Pro"}
                onNavigate={handleNavigateAfterEdit}
                navPage='home'
              />
            </span>
          </div>

          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!(p.previewEditMode && p.activeEditField)) {
                setMobileMenuOpen(!mobileMenuOpen);
              }
            }}
            className='md:hidden p-2'
          >
            <svg
              className='w-6 h-6 text-white'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M4 6h16M4 12h16M4 18h16'
              />
            </svg>
          </button>

          <div className='hidden md:flex items-center gap-6'>
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm font-medium transition-all duration-300 relative group ${
                  activePage === item.page
                    ? "text-blue-300"
                    : "text-white/80 hover:text-white"
                }`}
              >
                <InlineEditableTextWithNav
                  p={p}
                  field={`nav_${item.page}`}
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                  className='text-inherit'
                />
                <span
                  className={`absolute -bottom-1 left-0 h-0.5 bg-blue-400 transition-all duration-300 ${activePage === item.page ? "w-full" : "w-0 group-hover:w-full"}`}
                />
              </button>
            ))}
          </div>

          <div className='flex items-center gap-3'>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.handleGoToWishlist?.();
                }
              }}
              className='relative group'
            >
              <div className='p-2 rounded-full transition-all duration-300 hover:bg-blue-800/50'>
                <FaHeart className='text-lg text-white/80 hover:text-white transition-colors' />
              </div>
              {p.wishlistCount > 0 && (
                <span className='absolute -top-1 -right-1 w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center font-bold animate-pulse'>
                  {p.wishlistCount}
                </span>
              )}
            </button>
            <CartBtn p={p} dark={true} />
            {p.userMenuElement}
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className='md:hidden absolute top-full left-0 right-0 bg-[#1a365d] shadow-lg py-4 px-4 flex flex-col gap-2 z-50 border-t border-blue-800/30'>
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm py-2 px-3 rounded-lg text-left ${
                  activePage === item.page
                    ? "bg-blue-800/30 text-blue-300"
                    : "text-white/80 hover:text-white"
                }`}
              >
                <InlineEditableTextWithNav
                  p={p}
                  field={`nav_${item.page}`}
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                  className='text-inherit'
                />
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* ==================== HOME PAGE ==================== */}
      {activePage === "home" && (
        <>
          {/* Hero Section - FIXED: DIRECTLY USING CONFIG IMAGES */}
          <section className='relative w-full h-screen min-h-[700px] overflow-hidden -mt-[60px]'>
            <div className='absolute inset-0 w-full h-full'>
              {stationeryHeroImageConfigs.map((img, idx) => (
                <div
                  key={img.field}
                  className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${
                    idx === activeSlide ? "opacity-100 z-10" : "opacity-0 z-0"
                  }`}
                >
                  <InlineEditableImage p={p} field={img.field} className="h-full w-full">
                    <img
                      src={p.storeTheme?.[img.field] || img.src}
                      alt={`Stationery ${idx + 1}`}
                      className='w-full h-full object-cover object-center'
                      style={{
                        transform: `translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px) scale(1.05)`,
                        transition: "transform 0.3s ease-out",
                      }}
                    />
                  </InlineEditableImage>
                </div>
              ))}
              <div className="absolute inset-0 bg-gradient-to-r from-[#1a365d]/90 via-[#1a365d]/60 to-transparent z-20 pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-50 via-transparent to-transparent z-20 pointer-events-none" />
              
              {/* Hero edit buttons */}
              {p.previewEditMode && (
                <div className='absolute bottom-24 left-1/2 -translate-x-1/2 z-30 flex flex-wrap items-center gap-2 rounded-2xl border border-blue-300/40 bg-[#1a365d]/35 px-3 py-2 backdrop-blur-sm'>
                  {stationeryHeroImageConfigs.map((image, index) => (
                    <button
                      key={image.field}
                      type='button'
                      onClick={(event) => handleEditHeroImage(event, image.field)}
                      className={`rounded-full px-3 py-1 text-xs font-medium transition ${index === activeSlide ? "bg-blue-500 text-white" : "bg-white/10 text-blue-100 hover:bg-white/20"}`}
                    >
                      Hero {index + 1}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className='relative z-30 h-full w-full px-4 md:px-8 flex items-center'>
              <div className='max-w-3xl'>
                <div className='inline-block mb-6 animate-bounce-in'>
                  <div className='flex items-center gap-3 px-5 py-2.5 bg-[#1a365d]/40 backdrop-blur-md rounded-full border border-blue-400/30'>
                    <span className='w-2 h-2 rounded-full bg-blue-400 animate-ping' />
                    <span className='text-blue-200 text-xs tracking-[0.2em] uppercase'>
                      <InlineEditableTextWithNav
                        p={p}
                        field='heroEyebrow'
                        value={copy.eyebrow}
                        onNavigate={handleNavigateAfterEdit}
                        navPage='home'
                      />
                    </span>
                    <span className='w-2 h-2 rounded-full bg-blue-400 animate-pulse' />
                  </div>
                </div>
                <h1 className='text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.05] tracking-tight'>
                  <InlineEditableTextWithNav
                    p={p}
                    field='heroTitle'
                    value={copy.title}
                    onNavigate={handleNavigateAfterEdit}
                    navPage='home'
                  >
                    {copy.title.split(" ").map((word, i) => (
                      <span
                        key={i}
                        className='inline-block animate-word-pop'
                        style={{ animationDelay: `${i * 0.15}s` }}
                      >
                        {word}{" "}
                      </span>
                    ))}
                  </InlineEditableTextWithNav>
                </h1>
                <p className='mt-6 text-blue-200/90 text-lg max-w-xl animate-slide-right'>
                  <InlineEditableTextWithNav
                    p={p}
                    field='heroSubtitle'
                    value={copy.subtitle}
                    onNavigate={handleNavigateAfterEdit}
                    navPage='home'
                  />
                </p>
                <div className='mt-10 flex flex-wrap gap-4'>
                  <button
                    onClick={(e) => navigateTo("school", e)}
                    className='group px-8 py-4 rounded-full bg-blue-600 text-white font-semibold tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-blue-600/30 flex items-center gap-2 animate-slide-up'
                  >
                    <InlineEditableTextWithNav
                      p={p}
                      field='primaryCtaText'
                      value={copy.primaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage='school'
                    />
                    <span className='group-hover:translate-x-1 transition-transform'>
                      →
                    </span>
                  </button>
                  <button
                    onClick={(e) => navigateTo("art", e)}
                    className='px-8 py-4 rounded-full border-2 border-blue-400/50 text-blue-200 font-semibold tracking-wide transition-all duration-300 hover:bg-blue-600/20 hover:border-blue-300 backdrop-blur-sm animate-slide-up'
                  >
                    <InlineEditableTextWithNav
                      p={p}
                      field='secondaryCtaText'
                      value={copy.secondaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage='art'
                    />
                  </button>
                </div>
              </div>
            </div>

            <div className='absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce-slow z-30'>
              <div className='flex flex-col items-center gap-1'>
                <span className='text-blue-200/60 text-[10px] tracking-wider'>
                  <InlineEditableTextWithNav
                    p={p}
                    field='scroll_text'
                    value='SCROLL TO EXPLORE'
                    onNavigate={handleNavigateAfterEdit}
                    navPage='home'
                  />
                </span>
                <div className='w-5 h-8 border-2 border-blue-400/30 rounded-full flex justify-center'>
                  <div className='w-1 h-2 bg-blue-400/60 rounded-full mt-1 animate-scroll-dot' />
                </div>
              </div>
            </div>
          </section>

          {/* Featured Badge */}
          <div className='relative z-10 -mt-8 text-center'>
            <div className='inline-flex items-center gap-3 px-6 py-3 bg-white rounded-full shadow-lg border border-blue-200'>
              <span className='text-blue-600 text-sm'>📚</span>
              <span className='text-[#1a365d] text-xs font-semibold tracking-wider'>
                <InlineEditableTextWithNav
                  p={p}
                  field='collection_badge'
                  value='QUALITY STATIONERY SINCE 1995'
                  onNavigate={handleNavigateAfterEdit}
                  navPage='home'
                />
              </span>
              <span className='text-blue-600 text-sm'>📚</span>
            </div>
          </div>

          {/* Category Grid */}
          <section className='w-full px-4 md:px-8 py-12'>
            <div className='grid grid-cols-2 md:grid-cols-5 gap-4'>
              {productCategories.map((cat, idx) => (
                <div
                  key={cat.key}
                  className='group cursor-pointer animate-card-fly'
                  style={{ animationDelay: `${idx * 0.08}s` }}
                  onClick={(e) => {
                    if (cat.key === "all") {
                      navigateTo("school", e);
                    } else {
                      setActiveCategory(cat.key);
                      navigateTo(
                        cat.key === "school"
                          ? "school"
                          : cat.key === "office"
                            ? "office"
                            : cat.key === "art"
                              ? "art"
                              : "accessories",
                        e,
                      );
                    }
                  }}
                >
                  <div className='relative overflow-hidden rounded-2xl p-4 text-center transition-all duration-500 hover:-translate-y-2 hover:shadow-xl bg-white border border-blue-200 hover:border-blue-400'>
                    <div className='text-3xl mb-2 transition-all duration-300 group-hover:scale-110'>
                      {cat.icon}
                    </div>
                    <h3 className='font-semibold text-[#1a365d] text-xs'>
                      <InlineEditableTextWithNav
                        p={p}
                        field={`cat_${cat.key}`}
                        value={cat.label}
                        onNavigate={handleNavigateAfterEdit}
                        navPage='school'
                      />
                    </h3>
                    <div
                      className='mt-1 h-0.5 w-0 group-hover:w-full transition-all duration-500 mx-auto'
                      style={{ backgroundColor: cat.color }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Featured Products - FIXED with InlineEditableImage - REMOVED ADD TO CART */}
          <section className='w-full px-4 md:px-8 py-8 bg-white'>
            <div className='text-center mb-10'>
              <span className='text-blue-700 text-sm tracking-[0.2em] uppercase'>
                <InlineEditableTextWithNav
                  p={p}
                  field='featured_badge'
                  value='Our Products'
                  onNavigate={handleNavigateAfterEdit}
                  navPage='home'
                />
              </span>
              <h2 className='text-3xl md:text-4xl font-bold text-[#1a365d] mt-2'>
                <InlineEditableTextWithNav
                  p={p}
                  field='featured_title'
                  value='Popular Stationery'
                  onNavigate={handleNavigateAfterEdit}
                  navPage='home'
                />
              </h2>
              <div className='w-24 h-1 bg-gradient-to-r from-blue-600 to-blue-400 mx-auto mt-4 rounded-full' />
            </div>

            <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
              {editableStationeryProducts.slice(0, 4).map((product, idx) => {
                const entrance = getCardEntrance(idx);
                const isVisible = animationComplete;
                return (
                  <div
                    key={product.id}
                    className={`group cursor-pointer transition-all duration-700 ${isVisible ? "opacity-100 translate-x-0 translate-y-0 rotate-0" : `opacity-0 translate-x-[${entrance.x}px] translate-y-[${entrance.y}px] rotate-[${entrance.rotate}deg]`}`}
                    onMouseEnter={() => setHoveredCard(idx)}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => setSelectedProduct(product)}
                  >
                    <div className='relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-blue-100 hover:border-blue-300'>
                      <div className='relative h-48 overflow-hidden bg-blue-50'>
                        <InlineEditableImage p={p} field={product.imageField} className='block h-full w-full overflow-hidden'>
                          <img
                            src={product.image}
                            alt={product.name}
                            className='w-full h-full object-cover transition-transform duration-700 group-hover:scale-110'
                          />
                        </InlineEditableImage>
                        {product.badge && (
                          <div
                            className='absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg'
                            style={{ backgroundColor: product.badgeColor }}
                          >
                            {product.badge}
                          </div>
                        )}
                        <div className='absolute top-2 right-2 bg-[#1a365d]/80 text-blue-100 text-[10px] font-bold px-2 py-0.5 rounded-full'>
                          {product.brand}
                        </div>
                      </div>
                      <div className='p-3'>
                        <div className='flex items-center gap-1 mb-1'>
                          <span className='text-xs text-gray-500'>
                            {product.category}
                          </span>
                          <span className='text-xs text-gray-300'>•</span>
                          <span className='text-xs text-gray-500'>
                            {product.subCategory}
                          </span>
                        </div>
                        <h3 className='font-semibold text-gray-800 text-sm line-clamp-1'>
                          <InlineEditableTextWithNav
                            p={p}
                            field={product.nameKey}
                            value={product.name}
                            onNavigate={handleNavigateAfterEdit}
                            navPage='home'
                          />
                        </h3>
                        <div className='mt-2 flex items-center justify-between'>
                          <div>
                            <span className='text-lg font-bold text-blue-700'>
                              ₹
                              <InlineEditableTextWithNav
                                p={p}
                                field={product.priceKey}
                                value={product.price.toString()}
                                onNavigate={handleNavigateAfterEdit}
                                navPage='home'
                              />
                            </span>
                            {product.originalPrice > product.price && (
                              <span className='text-xs text-gray-400 line-through ml-1'>
                                ₹{product.originalPrice}
                              </span>
                            )}
                          </div>
                          <span className='text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full'>
                            {product.material ||
                              product.pages ||
                              product.colors}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className='text-center mt-10'>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!(p.previewEditMode && p.activeEditField)) {
                    setActiveCategory("all");
                    setActivePage("school");
                    setMobileMenuOpen(false);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }}
                className='px-8 py-3 rounded-full bg-blue-700 text-white font-semibold hover:bg-blue-800 transition-all duration-300 hover:scale-105'
              >
                <InlineEditableTextWithNav
                  p={p}
                  field='view_all_button'
                  value='View All Products'
                  onNavigate={handleNavigateAfterEdit}
                  navPage='school'
                />
              </button>
            </div>
          </section>

          {/* Learning Guides Preview - FIXED with InlineEditableImage */}
          <section className='w-full px-4 md:px-8 py-8'>
            <div className='text-center mb-8'>
              <span className='text-blue-700 text-sm tracking-[0.2em] uppercase'>
                <InlineEditableTextWithNav
                  p={p}
                  field='guides_badge'
                  value='Learning Guides'
                  onNavigate={handleNavigateAfterEdit}
                  navPage='guides'
                />
              </span>
            </div>
            <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
              {editableLearningGuides.map((guide) => (
                <div
                  key={guide.id}
                  className='group cursor-pointer rounded-2xl overflow-hidden bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-blue-200 hover:border-blue-400'
                  onClick={() => openGuideModal(guide)}
                >
                  <div className='h-40 overflow-hidden'>
                    <InlineEditableImage p={p} field={guide.imageField} className='block h-full w-full overflow-hidden'>
                      <img
                        src={guide.image}
                        alt={guide.title}
                        className='w-full h-full object-cover transition-transform duration-700 group-hover:scale-110'
                      />
                    </InlineEditableImage>
                  </div>
                  <div className='p-4'>
                    <h4 className='font-semibold text-[#1a365d] text-sm'>
                      <InlineEditableTextWithNav
                        p={p}
                        field={guide.titleKey}
                        value={guide.title}
                        onNavigate={handleNavigateAfterEdit}
                        navPage='guides'
                      />
                    </h4>
                    <p className='text-xs text-gray-600 mt-1'>
                      <InlineEditableTextWithNav
                        p={p}
                        field={guide.descKey}
                        value={guide.desc}
                        onNavigate={handleNavigateAfterEdit}
                        navPage='guides'
                      />
                    </p>
                    <div className='mt-2 flex items-center gap-2'>
                      <span className='text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full'>
                        {guide.difficulty}
                      </span>
                      <span className='text-xs text-blue-600 font-semibold group-hover:gap-2 transition-all flex items-center gap-1'>
                        Read More <span>→</span>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      {/* ==================== SCHOOL PAGE ==================== */}
      {activePage === "school" && (
        <section className='w-full px-4 md:px-8 py-12'>
          <div className='text-center mb-12'>
            <span className='text-yellow-700 text-sm tracking-[0.2em] uppercase'>
              <InlineEditableTextWithNav
                p={p}
                field='school_badge'
                value='School Supplies'
                onNavigate={handleNavigateAfterEdit}
                navPage='school'
              />
            </span>
            <h2 className='text-4xl md:text-5xl font-bold text-[#1a365d] mt-2'>
              <InlineEditableTextWithNav
                p={p}
                field='school_title'
                value='School Essentials'
                onNavigate={handleNavigateAfterEdit}
                navPage='school'
              />
            </h2>
            <p className='text-gray-600 mt-3 max-w-2xl mx-auto'>
              <InlineEditableTextWithNav
                p={p}
                field='school_subtitle'
                value='Everything students need for academic success'
                onNavigate={handleNavigateAfterEdit}
                navPage='school'
              />
            </p>
            <div className='w-24 h-1 bg-gradient-to-r from-yellow-600 to-yellow-400 mx-auto mt-4 rounded-full' />
          </div>

          <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'>
            {editableStationeryProducts
              .filter((d) => d.category === "School Supplies")
              .map((product) => (
                <div
                  key={product.id}
                  className='group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-yellow-200 hover:border-yellow-400'
                  onClick={() => setSelectedProduct(product)}
                >
                  <div className='relative h-48 overflow-hidden bg-yellow-50'>
                    <InlineEditableImage p={p} field={product.imageField} className='block h-full w-full overflow-hidden'>
                      <img
                        src={product.image}
                        alt={product.name}
                        className='w-full h-full object-cover transition-transform duration-700 group-hover:scale-110'
                      />
                    </InlineEditableImage>
                    {product.badge && (
                      <div
                        className='absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg'
                        style={{ backgroundColor: product.badgeColor }}
                      >
                        {product.badge}
                      </div>
                    )}
                    <div className='absolute top-2 right-2 bg-yellow-700/80 text-yellow-100 text-[10px] font-bold px-2 py-0.5 rounded-full'>
                      {product.pages || product.pieces || product.inkColor}
                    </div>
                  </div>
                  <div className='p-3'>
                    <div className='flex items-center gap-1 mb-1'>
                      <span className='text-xs text-gray-500'>
                        {product.brand}
                      </span>
                      <span className='text-xs text-gray-300'>•</span>
                      <span className='text-xs text-gray-500'>
                        {product.subCategory}
                      </span>
                    </div>
                    <h3 className='font-semibold text-gray-800 text-sm'>
                      <InlineEditableTextWithNav
                        p={p}
                        field={product.nameKey}
                        value={product.name}
                        onNavigate={handleNavigateAfterEdit}
                        navPage='school'
                      />
                    </h3>
                    <div className='mt-2 flex items-center justify-between'>
                      <span className='text-lg font-bold text-yellow-700'>
                        ₹
                        <InlineEditableTextWithNav
                          p={p}
                          field={product.priceKey}
                          value={product.price.toString()}
                          onNavigate={handleNavigateAfterEdit}
                          navPage='school'
                        />
                      </span>
                      {product.originalPrice > product.price && (
                        <span className='text-xs text-gray-400 line-through'>
                          ₹{product.originalPrice}
                        </span>
                      )}
                    </div>
                    <div className='mt-1 text-xs text-gray-500'>
                      {product.paperType ||
                        product.hardness ||
                        product.material}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </section>
      )}

      {/* ==================== OFFICE PAGE ==================== */}
      {activePage === "office" && (
        <section className='w-full px-4 md:px-8 py-12'>
          <div className='text-center mb-12'>
            <span className='text-blue-700 text-sm tracking-[0.2em] uppercase'>
              <InlineEditableTextWithNav
                p={p}
                field='office_badge'
                value='Office Supplies'
                onNavigate={handleNavigateAfterEdit}
                navPage='office'
              />
            </span>
            <h2 className='text-4xl md:text-5xl font-bold text-[#1a365d] mt-2'>
              <InlineEditableTextWithNav
                p={p}
                field='office_title'
                value='Office Essentials'
                onNavigate={handleNavigateAfterEdit}
                navPage='office'
              />
            </h2>
            <p className='text-gray-600 mt-3 max-w-2xl mx-auto'>
              <InlineEditableTextWithNav
                p={p}
                field='office_subtitle'
                value='Products for a productive workspace'
                onNavigate={handleNavigateAfterEdit}
                navPage='office'
              />
            </p>
            <div className='w-24 h-1 bg-gradient-to-r from-blue-600 to-blue-400 mx-auto mt-4 rounded-full' />
          </div>

          <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'>
            {editableStationeryProducts
              .filter((d) => d.category === "Office Supplies")
              .map((product) => (
                <div
                  key={product.id}
                  className='group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-blue-200 hover:border-blue-400'
                  onClick={() => setSelectedProduct(product)}
                >
                  <div className='relative h-48 overflow-hidden bg-blue-50'>
                    <InlineEditableImage p={p} field={product.imageField} className='block h-full w-full overflow-hidden'>
                      <img
                        src={product.image}
                        alt={product.name}
                        className='w-full h-full object-cover transition-transform duration-700 group-hover:scale-110'
                      />
                    </InlineEditableImage>
                    {product.badge && (
                      <div
                        className='absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg'
                        style={{ backgroundColor: product.badgeColor }}
                      >
                        {product.badge}
                      </div>
                    )}
                    <div className='absolute top-2 right-2 bg-blue-700/80 text-blue-100 text-[10px] font-bold px-2 py-0.5 rounded-full'>
                      {product.capacity || product.sheets}
                    </div>
                  </div>
                  <div className='p-3'>
                    <div className='flex items-center gap-1 mb-1'>
                      <span className='text-xs text-gray-500'>
                        {product.brand}
                      </span>
                      <span className='text-xs text-gray-300'>•</span>
                      <span className='text-xs text-gray-500'>
                        {product.subCategory}
                      </span>
                    </div>
                    <h3 className='font-semibold text-gray-800 text-sm'>
                      <InlineEditableTextWithNav
                        p={p}
                        field={product.nameKey}
                        value={product.name}
                        onNavigate={handleNavigateAfterEdit}
                        navPage='office'
                      />
                    </h3>
                    <div className='mt-2 flex items-center justify-between'>
                      <span className='text-lg font-bold text-blue-700'>
                        ₹
                        <InlineEditableTextWithNav
                          p={p}
                          field={product.priceKey}
                          value={product.price.toString()}
                          onNavigate={handleNavigateAfterEdit}
                          navPage='office'
                        />
                      </span>
                      {product.originalPrice > product.price && (
                        <span className='text-xs text-gray-400 line-through'>
                          ₹{product.originalPrice}
                        </span>
                      )}
                    </div>
                    <div className='mt-1 text-xs text-gray-500'>
                      {product.material} • {product.size || product.color}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </section>
      )}

      {/* ==================== ART & CRAFT PAGE ==================== */}
      {activePage === "art" && (
        <section className='w-full px-4 md:px-8 py-12'>
          <div className='text-center mb-12'>
            <span className='text-red-700 text-sm tracking-[0.2em] uppercase'>
              <InlineEditableTextWithNav
                p={p}
                field='art_badge'
                value='Art & Craft'
                onNavigate={handleNavigateAfterEdit}
                navPage='art'
              />
            </span>
            <h2 className='text-4xl md:text-5xl font-bold text-[#1a365d] mt-2'>
              <InlineEditableTextWithNav
                p={p}
                field='art_title'
                value='Creative Supplies'
                onNavigate={handleNavigateAfterEdit}
                navPage='art'
              />
            </h2>
            <p className='text-gray-600 mt-3 max-w-2xl mx-auto'>
              <InlineEditableTextWithNav
                p={p}
                field='art_subtitle'
                value='Everything you need for creative expression'
                onNavigate={handleNavigateAfterEdit}
                navPage='art'
              />
            </p>
            <div className='w-24 h-1 bg-gradient-to-r from-red-600 to-red-400 mx-auto mt-4 rounded-full' />
          </div>

          <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'>
            {editableStationeryProducts
              .filter((d) => d.category === "Art & Craft")
              .map((product) => (
                <div
                  key={product.id}
                  className='group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-red-200 hover:border-red-400'
                  onClick={() => setSelectedProduct(product)}
                >
                  <div className='relative h-48 overflow-hidden bg-red-50'>
                    <InlineEditableImage p={p} field={product.imageField} className='block h-full w-full overflow-hidden'>
                      <img
                        src={product.image}
                        alt={product.name}
                        className='w-full h-full object-cover transition-transform duration-700 group-hover:scale-110'
                      />
                    </InlineEditableImage>
                    {product.badge && (
                      <div
                        className='absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg'
                        style={{ backgroundColor: product.badgeColor }}
                      >
                        {product.badge}
                      </div>
                    )}
                    <div className='absolute top-2 right-2 bg-red-700/80 text-red-100 text-[10px] font-bold px-2 py-0.5 rounded-full'>
                      {product.colors || product.pages || product.sheets}
                    </div>
                  </div>
                  <div className='p-3'>
                    <div className='flex items-center gap-1 mb-1'>
                      <span className='text-xs text-gray-500'>
                        {product.brand}
                      </span>
                      <span className='text-xs text-gray-300'>•</span>
                      <span className='text-xs text-gray-500'>
                        {product.subCategory}
                      </span>
                    </div>
                    <h3 className='font-semibold text-gray-800 text-sm'>
                      <InlineEditableTextWithNav
                        p={p}
                        field={product.nameKey}
                        value={product.name}
                        onNavigate={handleNavigateAfterEdit}
                        navPage='art'
                      />
                    </h3>
                    <div className='mt-2 flex items-center justify-between'>
                      <span className='text-lg font-bold text-red-700'>
                        ₹
                        <InlineEditableTextWithNav
                          p={p}
                          field={product.priceKey}
                          value={product.price.toString()}
                          onNavigate={handleNavigateAfterEdit}
                          navPage='art'
                        />
                      </span>
                      {product.originalPrice > product.price && (
                        <span className='text-xs text-gray-400 line-through'>
                          ₹{product.originalPrice}
                        </span>
                      )}
                    </div>
                    <div className='mt-1 text-xs text-gray-500'>
                      {product.paperWeight || product.type}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </section>
      )}

      {/* ==================== ACCESSORIES PAGE ==================== */}
      {activePage === "accessories" && (
        <section className='w-full px-4 md:px-8 py-12'>
          <div className='text-center mb-12'>
            <span className='text-purple-700 text-sm tracking-[0.2em] uppercase'>
              <InlineEditableTextWithNav
                p={p}
                field='accessories_badge'
                value='Accessories'
                onNavigate={handleNavigateAfterEdit}
                navPage='accessories'
              />
            </span>
            <h2 className='text-4xl md:text-5xl font-bold text-[#1a365d] mt-2'>
              <InlineEditableTextWithNav
                p={p}
                field='accessories_title'
                value='Stationery Accessories'
                onNavigate={handleNavigateAfterEdit}
                navPage='accessories'
              />
            </h2>
            <p className='text-gray-600 mt-3 max-w-2xl mx-auto'>
              <InlineEditableTextWithNav
                p={p}
                field='accessories_subtitle'
                value='Essential accessories for organized learning'
                onNavigate={handleNavigateAfterEdit}
                navPage='accessories'
              />
            </p>
            <div className='w-24 h-1 bg-gradient-to-r from-purple-600 to-purple-400 mx-auto mt-4 rounded-full' />
          </div>

          <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'>
            {editableStationeryProducts
              .filter((d) => d.category === "Accessories")
              .map((product) => (
                <div
                  key={product.id}
                  className='group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-purple-200 hover:border-purple-400'
                  onClick={() => setSelectedProduct(product)}
                >
                  <div className='relative h-48 overflow-hidden bg-purple-50'>
                    <InlineEditableImage p={p} field={product.imageField} className='block h-full w-full overflow-hidden'>
                      <img
                        src={product.image}
                        alt={product.name}
                        className='w-full h-full object-cover transition-transform duration-700 group-hover:scale-110'
                      />
                    </InlineEditableImage>
                    {product.badge && (
                      <div
                        className='absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg'
                        style={{ backgroundColor: product.badgeColor }}
                      >
                        {product.badge}
                      </div>
                    )}
                    <div className='absolute top-2 right-2 bg-purple-700/80 text-purple-100 text-[10px] font-bold px-2 py-0.5 rounded-full'>
                      {product.material || product.quantity}
                    </div>
                  </div>
                  <div className='p-3'>
                    <div className='flex items-center gap-1 mb-1'>
                      <span className='text-xs text-gray-500'>
                        {product.brand}
                      </span>
                      <span className='text-xs text-gray-300'>•</span>
                      <span className='text-xs text-gray-500'>
                        {product.subCategory}
                      </span>
                    </div>
                    <h3 className='font-semibold text-gray-800 text-sm'>
                      <InlineEditableTextWithNav
                        p={p}
                        field={product.nameKey}
                        value={product.name}
                        onNavigate={handleNavigateAfterEdit}
                        navPage='accessories'
                      />
                    </h3>
                    <div className='mt-2 flex items-center justify-between'>
                      <span className='text-lg font-bold text-purple-700'>
                        ₹
                        <InlineEditableTextWithNav
                          p={p}
                          field={product.priceKey}
                          value={product.price.toString()}
                          onNavigate={handleNavigateAfterEdit}
                          navPage='accessories'
                        />
                      </span>
                      {product.originalPrice > product.price && (
                        <span className='text-xs text-gray-400 line-through'>
                          ₹{product.originalPrice}
                        </span>
                      )}
                    </div>
                    <div className='mt-1 text-xs text-gray-500'>
                      {product.color || product.type}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </section>
      )}

      {/* ==================== JOURNAL PAGE ==================== */}
      {activePage === "journal" && (
        <section className='w-full px-4 md:px-8 py-12'>
          <div className='text-center mb-12'>
            <span className='text-blue-700 text-sm tracking-[0.2em] uppercase'>
              <InlineEditableTextWithNav
                p={p}
                field='journal_badge'
                value='Journal'
                onNavigate={handleNavigateAfterEdit}
                navPage='journal'
              />
            </span>
            <h2 className='text-4xl md:text-5xl font-bold text-[#1a365d] mt-2'>
              <InlineEditableTextWithNav
                p={p}
                field='journal_title'
                value='Stationery Stories'
                onNavigate={handleNavigateAfterEdit}
                navPage='journal'
              />
            </h2>
            <p className='text-gray-600 mt-3 max-w-2xl mx-auto'>
              <InlineEditableTextWithNav
                p={p}
                field='journal_subtitle'
                value='Inspiration for writers, artists, and creators'
                onNavigate={handleNavigateAfterEdit}
                navPage='journal'
              />
            </p>
            <div className='w-24 h-1 bg-gradient-to-r from-blue-600 to-blue-400 mx-auto mt-4 rounded-full' />
          </div>

          <div className='grid md:grid-cols-3 gap-8'>
            {editableEditorialPosts.map((post) => (
              <div
                key={post.id}
                className='group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer border border-blue-200 hover:border-blue-400'
                onClick={(e) => openBlogModal(post, e)}
              >
                <div className='h-56 overflow-hidden'>
                  <InlineEditableImage p={p} field={post.imageField} className='block h-full w-full overflow-hidden'>
                    <img
                      src={post.image}
                      alt={post.title}
                      className='w-full h-full object-cover transition-transform duration-700 group-hover:scale-110'
                    />
                  </InlineEditableImage>
                </div>
                <div className='p-5'>
                  <div className='flex items-center justify-between mb-2'>
                    <span className='text-xs text-blue-600 font-semibold'>
                      {post.category}
                    </span>
                    <span className='text-xs text-gray-400'>
                      <InlineEditableTextWithNav
                        p={p}
                        field={post.dateKey}
                        value={post.date}
                        onNavigate={handleNavigateAfterEdit}
                        navPage='journal'
                      />
                    </span>
                  </div>
                  <h3 className='text-xl font-semibold text-[#1a365d] mb-2'>
                    <InlineEditableTextWithNav
                      p={p}
                      field={post.titleKey}
                      value={post.title}
                      onNavigate={handleNavigateAfterEdit}
                      navPage='journal'
                    />
                  </h3>
                  <p className='text-gray-600 text-sm line-clamp-2'>
                    <InlineEditableTextWithNav
                      p={p}
                      field={post.descKey}
                      value={post.desc}
                      onNavigate={handleNavigateAfterEdit}
                      navPage='journal'
                    />
                  </p>
                  <div className='mt-4 flex items-center justify-between'>
                    <span className='text-xs text-gray-400'>
                      <InlineEditableTextWithNav
                        p={p}
                        field='by_prefix'
                        value='By'
                        onNavigate={handleNavigateAfterEdit}
                        navPage='journal'
                      />{" "}
                      <span className='font-semibold text-blue-800'>
                        <InlineEditableTextWithNav
                          p={p}
                          field={post.authorKey}
                          value={post.author}
                          onNavigate={handleNavigateAfterEdit}
                          navPage='journal'
                        />
                      </span>
                    </span>
                    <button
                      onClick={(e) => openBlogModal(post, e)}
                      className='text-blue-700 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all'
                    >
                      <InlineEditableTextWithNav
                        p={p}
                        field='read_more_button'
                        value='Read More'
                        onNavigate={handleNavigateAfterEdit}
                        navPage='journal'
                      />
                      <span>→</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ==================== ABOUT PAGE ==================== */}
      {activePage === "about" && (
        <section className='w-full px-4 md:px-8 py-12 md:py-20'>
          <div className='grid md:grid-cols-2 gap-12 items-center'>
            <div>
              <span className='text-blue-700 text-sm tracking-wider uppercase'>
                <InlineEditableTextWithNav
                  p={p}
                  field='about_badge'
                  value='About Us'
                  onNavigate={handleNavigateAfterEdit}
                  navPage='about'
                />
              </span>
              <h2 className='text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a365d] mt-3'>
                <InlineEditableTextWithNav
                  p={p}
                  field='about_title'
                  value='Empowering Education & Creativity'
                  onNavigate={handleNavigateAfterEdit}
                  navPage='about'
                />
              </h2>
              <div className='w-16 h-1 bg-blue-600 mt-4 mb-6' />
              <p className='text-gray-600 leading-relaxed mb-4'>
                <InlineEditableTextWithNav
                  p={p}
                  field='about_para1'
                  value='Stationery Pro has been the trusted destination for quality stationery products for over two decades. From school supplies to office essentials, we are committed to providing products that inspire learning, creativity, and productivity.'
                  onNavigate={handleNavigateAfterEdit}
                  navPage='about'
                />
              </p>
              <p className='text-gray-600 leading-relaxed mb-6'>
                <InlineEditableTextWithNav
                  p={p}
                  field='about_para2'
                  value="We believe that the right tools make all the difference. Whether you're a student preparing for exams, an artist creating masterpieces, or a professional organizing your workspace, we have the products you need to succeed."
                  onNavigate={handleNavigateAfterEdit}
                  navPage='about'
                />
              </p>
              <div className='flex flex-wrap gap-6'>
                <div>
                  <p className='text-3xl font-bold text-blue-700'>
                    <InlineEditableTextWithNav
                      p={p}
                      field='about_stat1_value'
                      value='500+'
                      onNavigate={handleNavigateAfterEdit}
                      navPage='about'
                    />
                  </p>
                  <p className='text-sm text-gray-500'>
                    <InlineEditableTextWithNav
                      p={p}
                      field='about_stat1_label'
                      value='Products'
                      onNavigate={handleNavigateAfterEdit}
                      navPage='about'
                    />
                  </p>
                </div>
                <div>
                  <p className='text-3xl font-bold text-blue-700'>
                    <InlineEditableTextWithNav
                      p={p}
                      field='about_stat2_value'
                      value='30+'
                      onNavigate={handleNavigateAfterEdit}
                      navPage='about'
                    />
                  </p>
                  <p className='text-sm text-gray-500'>
                    <InlineEditableTextWithNav
                      p={p}
                      field='about_stat2_label'
                      value='Trusted Brands'
                      onNavigate={handleNavigateAfterEdit}
                      navPage='about'
                    />
                  </p>
                </div>
                <div>
                  <p className='text-3xl font-bold text-blue-700'>
                    <InlineEditableTextWithNav
                      p={p}
                      field='about_stat3_value'
                      value='10k+'
                      onNavigate={handleNavigateAfterEdit}
                      navPage='about'
                    />
                  </p>
                  <p className='text-sm text-gray-500'>
                    <InlineEditableTextWithNav
                      p={p}
                      field='about_stat3_label'
                      value='Happy Customers'
                      onNavigate={handleNavigateAfterEdit}
                      navPage='about'
                    />
                  </p>
                </div>
              </div>
            </div>
            <div className='rounded-2xl overflow-hidden shadow-2xl'>
              <InlineEditableImage p={p} field='stationeryAboutImage' className='block h-full w-full overflow-hidden'>
                <img
                  src={p.storeTheme?.stationeryAboutImage || "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/de75e355-b676-475f-bcb4-4482eec4035b_art_pencil.jpg"}
                  alt='About Stationery Pro'
                  className='w-full h-full object-cover'
                />
              </InlineEditableImage>
            </div>
          </div>
        </section>
      )}

      {/* ==================== PRODUCT TABS ==================== */}
      <section className='w-full px-4 md:px-8 mt-8'>
        <div className='flex justify-center gap-2 flex-wrap'>
          {[
            ["products", "📚 Shop Stationery"],
            ["services", "🛠 Services"],
            ["details", "📖 About Store"],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.setActiveTab(key);
                }
              }}
              className={`px-5 md:px-7 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${p.activeTab === key ? "bg-blue-700 text-white shadow-lg" : "text-gray-500 hover:text-blue-700"}`}
            >
              <InlineEditableTextWithNav
                p={p}
                field={`tab_${key}_label`}
                value={label}
                onNavigate={handleNavigateAfterEdit}
                navPage={activePage}
              />
            </button>
          ))}
        </div>
      </section>

      {/* Main Content */}
      <section className='flex-1 w-full px-4 md:px-8 py-8 md:py-12'>
        <TabContent p={p} />
      </section>

      <StoreFooter p={p} />

      {/* ==================== MODALS ==================== */}

      {/* Product Modal - FIXED with InlineEditableImage - REMOVED ADD TO CART */}
      {selectedProduct && (
        <div
          className='fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in'
          onClick={handleBackdropClick}
        >
          <div
            className='max-w-md w-full bg-white rounded-2xl overflow-hidden shadow-2xl animate-slide-up'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='relative h-64'>
              <InlineEditableImage p={p} field={selectedProduct.imageField} className='block h-full w-full overflow-hidden'>
                <img
                  src={selectedProduct.image}
                  alt={selectedProduct.name}
                  className='w-full h-full object-cover'
                />
              </InlineEditableImage>
              <button
                onClick={() => setSelectedProduct(null)}
                className='absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 text-white hover:bg-blue-600 transition flex items-center justify-center text-xl'
              >
                ✕
              </button>
              {selectedProduct.badge && (
                <div
                  className='absolute top-3 left-3 px-3 py-1 rounded-full text-white text-xs font-bold shadow-lg'
                  style={{ backgroundColor: selectedProduct.badgeColor }}
                >
                  {selectedProduct.badge}
                </div>
              )}
              <div className='absolute bottom-3 right-3 bg-[#1a365d]/80 text-blue-100 text-xs font-bold px-2 py-1 rounded-full'>
                {selectedProduct.brand}
              </div>
            </div>
            <div className='p-6'>
              <div className='flex items-center gap-2 mb-2'>
                <span className='text-xs text-gray-500'>
                  {selectedProduct.category}
                </span>
                <span className='text-xs text-gray-300'>•</span>
                <span className='text-xs text-gray-500'>
                  {selectedProduct.subCategory}
                </span>
              </div>
              <h3 className='text-2xl font-bold text-[#1a365d]'>
                <InlineEditableTextWithNav
                  p={p}
                  field={selectedProduct.nameKey}
                  value={selectedProduct.name}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </h3>
              <p className='text-gray-600 text-sm mt-2'>
                <InlineEditableTextWithNav
                  p={p}
                  field={selectedProduct.descriptionKey}
                  value={selectedProduct.description}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </p>
              <div className='mt-4 flex flex-wrap gap-2'>
                <span className='text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full'>
                  {selectedProduct.subCategory}
                </span>
                {selectedProduct.pages && (
                  <span className='text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full'>
                    {selectedProduct.pages} pages
                  </span>
                )}
                {selectedProduct.colors && (
                  <span className='text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full'>
                    {selectedProduct.colors} colors
                  </span>
                )}
              </div>
              <div className='mt-4 flex items-center gap-3'>
                <span className='text-3xl font-bold text-blue-700'>
                  ₹
                  <InlineEditableTextWithNav
                    p={p}
                    field={selectedProduct.priceKey}
                    value={selectedProduct.price.toString()}
                    onNavigate={handleNavigateAfterEdit}
                    navPage={activePage}
                    isInModal={true}
                  />
                </span>
                {selectedProduct.originalPrice > selectedProduct.price && (
                  <span className='text-sm text-gray-400 line-through'>
                    ₹
                    <InlineEditableTextWithNav
                      p={p}
                      field={selectedProduct.originalPriceKey}
                      value={selectedProduct.originalPrice.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage={activePage}
                      isInModal={true}
                    />
                  </span>
                )}
              </div>
              <div className='mt-4 text-sm text-blue-600 text-center bg-blue-50 p-3 rounded-full border border-blue-200'>
                💝 A perfect stationery item for your needs
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Guide Modal - FIXED with InlineEditableImage */}
      {selectedGuide && (
        <div
          className='fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in'
          onClick={handleBackdropClick}
        >
          <div
            className='max-w-2xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl animate-slide-up max-h-[90vh] overflow-y-auto'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='sticky top-0 z-10 bg-white border-b border-gray-100 p-4 flex justify-between items-center'>
              <h3 className='text-lg font-semibold text-[#1a365d]'>
                Learning Guide
              </h3>
              <button
                onClick={closeGuideModal}
                className='w-10 h-10 rounded-full bg-gray-100 text-gray-600 hover:bg-blue-600 hover:text-white transition flex items-center justify-center text-xl'
              >
                ✕
              </button>
            </div>
            <div className='p-6'>
              <div className='mb-6'>
                <InlineEditableImage p={p} field={selectedGuide.imageField} className='block w-full overflow-hidden rounded-xl'>
                  <img
                    src={selectedGuide.image}
                    alt={selectedGuide.title}
                    className='w-full h-64 object-cover'
                  />
                </InlineEditableImage>
              </div>
              <div className='mb-3'>
                <span className='inline-block px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-semibold'>
                  {selectedGuide.difficulty}
                </span>
              </div>
              <h2 className='text-2xl md:text-3xl font-bold text-[#1a365d] mb-4'>
                <InlineEditableTextWithNav
                  p={p}
                  field={selectedGuide.titleKey}
                  value={selectedGuide.title}
                  onNavigate={handleNavigateAfterEdit}
                  navPage='guides'
                  isInModal={true}
                />
              </h2>
              <p className='text-gray-600 mb-6'>
                <InlineEditableTextWithNav
                  p={p}
                  field={selectedGuide.descKey}
                  value={selectedGuide.desc}
                  onNavigate={handleNavigateAfterEdit}
                  navPage='guides'
                  isInModal={true}
                />
              </p>
              <div className='space-y-4'>
                <p className='font-semibold text-[#1a365d]'>Steps:</p>
                {selectedGuide.steps?.map((step, idx) => (
                  <div
                    key={idx}
                    className='flex items-start gap-3 p-3 bg-blue-50 rounded-xl'
                  >
                    <span className='w-8 h-8 rounded-full bg-blue-700 text-white flex items-center justify-center font-bold text-sm flex-shrink-0'>
                      {idx + 1}
                    </span>
                    <p className='text-gray-700 text-sm'>{step}</p>
                  </div>
                ))}
              </div>
              <div className='mt-6 flex justify-end'>
                <button
                  onClick={closeGuideModal}
                  className='px-6 py-2 rounded-full bg-blue-700 text-white hover:bg-blue-800 transition'
                >
                  <InlineEditableTextWithNav
                    p={p}
                    field='close_button'
                    value='Close'
                    onNavigate={handleNavigateAfterEdit}
                    navPage='guides'
                    isInModal={true}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Blog Modal - FIXED with InlineEditableImage */}
      {selectedPost && (
        <div
          className='fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in'
          onClick={handleBackdropClick}
        >
          <div
            className='max-w-2xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='sticky top-0 z-10 bg-white border-b border-gray-100 p-4 flex justify-between items-center'>
              <h3 className='text-lg font-semibold text-[#1a365d]'>
                Stationery Stories
              </h3>
              <button
                onClick={closeBlogModal}
                className='w-10 h-10 rounded-full bg-gray-100 text-gray-600 hover:bg-blue-600 hover:text-white transition flex items-center justify-center text-xl'
              >
                ✕
              </button>
            </div>
            <div className='p-6'>
              <div className='mb-6'>
                <InlineEditableImage p={p} field={selectedPost.imageField} className='block w-full overflow-hidden rounded-xl'>
                  <img
                    src={selectedPost.image}
                    alt={selectedPost.title}
                    className='w-full h-64 object-cover'
                  />
                </InlineEditableImage>
              </div>
              <div className='mb-3'>
                <span className='inline-block px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-semibold'>
                  {selectedPost.category}
                </span>
              </div>
              <h2 className='text-2xl md:text-3xl font-bold text-[#1a365d] mb-2'>
                <InlineEditableTextWithNav
                  p={p}
                  field={selectedPost.titleKey}
                  value={selectedPost.title}
                  onNavigate={handleNavigateAfterEdit}
                  navPage='journal'
                  isInModal={true}
                />
              </h2>
              <div className='flex flex-wrap items-center gap-3 text-sm text-gray-500 border-b border-gray-100 pb-4 mb-4'>
                <span>
                  <InlineEditableTextWithNav
                    p={p}
                    field={selectedPost.dateKey}
                    value={selectedPost.date}
                    onNavigate={handleNavigateAfterEdit}
                    navPage='journal'
                    isInModal={true}
                  />
                </span>
                <span>•</span>
                <span>
                  <InlineEditableTextWithNav
                    p={p}
                    field='by_prefix'
                    value='By'
                    onNavigate={handleNavigateAfterEdit}
                    navPage='journal'
                    isInModal={true}
                  />{" "}
                  <span className='font-semibold text-blue-800'>
                    <InlineEditableTextWithNav
                      p={p}
                      field={selectedPost.authorKey}
                      value={selectedPost.author}
                      onNavigate={handleNavigateAfterEdit}
                      navPage='journal'
                      isInModal={true}
                    />
                  </span>
                </span>
              </div>
              <div className='prose prose-lg max-w-none'>
                <p className='text-gray-600 leading-relaxed whitespace-pre-wrap'>
                  <InlineEditableTextWithNav
                    p={p}
                    field={selectedPost.fullContentKey}
                    value={selectedPost.fullContent}
                    onNavigate={handleNavigateAfterEdit}
                    navPage='journal'
                    isInModal={true}
                  />
                </p>
              </div>
              <div className='mt-8 pt-4 border-t border-gray-100 flex justify-end'>
                <button
                  onClick={closeBlogModal}
                  className='px-6 py-2 rounded-full bg-blue-700 text-white hover:bg-blue-800 transition'
                >
                  <InlineEditableTextWithNav
                    p={p}
                    field='close_button'
                    value='Close'
                    onNavigate={handleNavigateAfterEdit}
                    navPage='journal'
                    isInModal={true}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes marquee {
          0% {
            transform: translateX(100%);
          }
          100% {
            transform: translateX(-100%);
          }
        }
        .animate-marquee {
          animation: marquee 20s linear infinite;
        }

        @keyframes float-particle {
          0%,
          100% {
            transform: translateY(0px) translateX(0px);
            opacity: 0.05;
          }
          50% {
            transform: translateY(-40px) translateX(20px);
            opacity: 0.2;
          }
        }
        .animate-float-particle {
          animation: float-particle 6s ease-in-out infinite;
        }

        @keyframes word-pop {
          0% {
            opacity: 0;
            transform: scale(0.5) rotate(-8deg);
          }
          60% {
            transform: scale(1.08) rotate(2deg);
          }
          100% {
            opacity: 1;
            transform: scale(1) rotate(0);
          }
        }
        .animate-word-pop {
          animation: word-pop 0.6s cubic-bezier(0.34, 1.2, 0.64, 1) forwards;
          opacity: 0;
        }

        @keyframes slide-right {
          from {
            opacity: 0;
            transform: translateX(-40px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-slide-right {
          animation: slide-right 0.7s ease-out forwards;
          opacity: 0;
          animation-fill-mode: forwards;
        }

        @keyframes bounce-in {
          0% {
            opacity: 0;
            transform: scale(0.3);
          }
          50% {
            transform: scale(1.05);
          }
          70% {
            transform: scale(0.95);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-bounce-in {
          animation: bounce-in 0.7s cubic-bezier(0.34, 1.2, 0.64, 1) forwards;
          opacity: 0;
        }

        @keyframes card-fly {
          0% {
            opacity: 0;
            transform: translateY(40px) scale(0.9);
          }
          60% {
            transform: translateY(-5px) scale(1.02);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-card-fly {
          animation: card-fly 0.6s cubic-bezier(0.34, 1.2, 0.64, 1) forwards;
          opacity: 0;
        }

        @keyframes scroll-dot {
          0% {
            transform: translateY(0);
            opacity: 1;
          }
          100% {
            transform: translateY(12px);
            opacity: 0;
          }
        }
        .animate-scroll-dot {
          animation: scroll-dot 1.5s ease-in-out infinite;
        }

        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.5s ease-out forwards;
          opacity: 0;
          animation-fill-mode: forwards;
        }

        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }

        @keyframes bounce {
          0%,
          100% {
            transform: translateX(-50%) translateY(0);
          }
          50% {
            transform: translateX(-50%) translateY(-8px);
          }
        }
        .animate-bounce-slow {
          animation: bounce 2s ease-in-out infinite;
        }

        @keyframes slide-up-modal {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-slide-up {
          animation: slide-up-modal 0.4s ease-out forwards;
        }

        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}